import { Box, Typography, useMediaQuery } from "@mui/material";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import gearIcon from "../../../../../../../assets/TailoredGeneration/icons/tg-gear-icon.svg";
import InfoIcon from "../../../../../../../assets/images/icons/InfoIcon.tsx";
import { PaginationOptions } from "../../../../../../../components/common/BriaTable/BriaTable.tsx";
import BriaTooltip from "../../../../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import TableLayout from "../../../../../../../layout/TableLayout/TableLayout.tsx";
import { TgModel } from "../../../../../Models/store/tg-model-store.tsx";
import CheckpointDropdown from "../CheckpointDropdown/CheckpointDropdown.tsx";
import { useModelTableHelpers } from "../hooks/useModelTableHelpers.tsx";
import columnStyles from "../shared/TgModelsTableColumns.module.scss";
import { TgExpertModelTableRowType } from "../types.ts";
import styles from "./ExpertModelsTable.module.scss";

interface ExpertModelsTableProps {
	models: TgModel[];
	loadingRow?: { data: TgModel; column: string };
	setLoadingRow: (loadingRow: { data: TgModel; column: string } | undefined) => void;
	setShowTrainingParametersCard: (card: { model: TgModel; anchorEl: HTMLElement } | null) => void;
	setShowChangeVersionPopup: (
		popup: {
			model: TgModel;
			currentCheckpointStep: string;
			checkpointStepToBeChanged: string;
			setSelectedCheckpoint: any;
		} | null,
	) => void;
	showTrainingParametersCard?: { model: TgModel; anchorEl: HTMLElement } | null;
	paginationOptions: PaginationOptions<TgModel>;
}

const ExpertModelsTable = observer(
	({
		models,
		loadingRow,
		setLoadingRow,
		setShowTrainingParametersCard,
		setShowChangeVersionPopup,
		showTrainingParametersCard,
		paginationOptions,
	}: ExpertModelsTableProps) => {
		const { t } = useTranslation("tg", { keyPrefix: "models.dashboard" });
		const {
			getPossibleAction,
			getCreationBy,
			getIntegrateNowButton,
			getNameColumn,
			getStatusColumn,
			getActionsColumn,
			getFeedbackCountCell,
		} = useModelTableHelpers();

		const isSmallScreen = useMediaQuery("(max-width:1540px)");

		// Headers for Expert Models table (responsive)
		const expertTableHeaderArray = isSmallScreen
			? [
					t("table.headers.name"),
					t("table.headers.id"),
					t("table.headers.creation_by"),
					t("table.headers.status"),
					t("table.headers.model_parameters"),
					t("table.headers.active_model_version"),
					t("table.headers.feedback_count"),
					t("table.headers.actions"),
					"",
				]
			: [
					t("table.headers.name"),
					t("table.headers.id"),
					t("table.headers.creation_by"),
					t("table.headers.status"),
					t("table.headers.model_parameters"),
					t("table.headers.active_model_version"),
					t("table.headers.feedback_count"),
					t("table.headers.actions"),
					t("table.headers.get_access"),
					t("table.headers.possible_action"),
				];

		const headerComponents = {
			model_parameters: (() => {
				const headerLabel = t("table.headers.model_parameters");
				const lastSpaceIdx = headerLabel.lastIndexOf(" ");
				const firstPart = lastSpaceIdx > 0 ? headerLabel.slice(0, lastSpaceIdx) : headerLabel;
				const secondPart = lastSpaceIdx > 0 ? headerLabel.slice(lastSpaceIdx + 1) : "";
				return (
					<>
						{/* large screen: original single-line */}
						<Box className={styles.activeModelHeaderDesktop}>{t("table.headers.model_parameters")}</Box>

						{/* small screen: two lines */}
						<Box className={styles.activeModelHeaderMobile}>
							<Box className={styles.activeModelHeaderMobileText}>
								<span>{firstPart}</span>
								{secondPart ? <span>{secondPart}</span> : null}
							</Box>
						</Box>
					</>
				);
			})(),
			active_model_version: (() => {
				const headerLabel = t("table.headers.active_model_version");
				const lastSpaceIdx = headerLabel.lastIndexOf(" ");
				const firstPart = lastSpaceIdx > 0 ? headerLabel.slice(0, lastSpaceIdx) : headerLabel;
				const secondPart = lastSpaceIdx > 0 ? headerLabel.slice(lastSpaceIdx + 1) : "";
				return (
					<>
						{/* large screen: original single-line */}
						<Box className={styles.activeModelHeaderDesktop}>
							{t("table.headers.active_model_version")}
							<BriaTooltip
								title={t(
									"table.headers.active_model_version_info",
									"The checkpoint that is used for inference. the Default checkpoint is the last step of the training.",
								)}
								className={styles.info}
								displayOn="click"
							>
								<InfoIcon className={styles.infoIcon} />
							</BriaTooltip>
						</Box>

						{/* small screen: two columns (left stacked text, right centered tooltip) */}
						<Box className={styles.activeModelHeaderMobile}>
							<Box className={styles.activeModelHeaderMobileText}>
								<span>{firstPart}</span>
								{secondPart ? <span>{secondPart}</span> : null}
							</Box>
							<Box className={styles.activeModelHeaderMobileRight}>
								<BriaTooltip
									title={t(
										"table.headers.active_model_version_info",
										"The checkpoint that is used for inference. the Default checkpoint is the last step of the training.",
									)}
									className={styles.info}
									displayOn="click"
								>
									<InfoIcon className={styles.infoIcon} />
								</BriaTooltip>
							</Box>
						</Box>
					</>
				);
			})(),
		};

		const createExpertRow = (model: TgModel): TgExpertModelTableRowType => {
			const model_parameters = (
				<Box>
					<Box>
						<img
							alt={"parameters"}
							src={gearIcon}
							className={`${styles.gearIcon} ${
								showTrainingParametersCard?.model.id === model.id ? styles.clicked : ""
							}`}
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								setShowTrainingParametersCard({
									model: model,
									anchorEl: e.currentTarget as HTMLElement,
								});
							}}
						/>
					</Box>
				</Box>
			);

			const active_model_version =
				model.status == "Completed" ? (
					<CheckpointDropdown model={model} setShowChangeVersionPopup={setShowChangeVersionPopup} />
				) : model.status == "InProgress" ? (
					<Box>{t(`table.active_model_version.in_training`)}</Box>
				) : (
					<Box />
				);

			return {
				key: model.id,
				data: (() => {
					const base: any = {
						name: getNameColumn(model),
						id: model.id,
						creation_by: getCreationBy(model),
						status: getStatusColumn(model),
						model_parameters,
						active_model_version,
						feedback_count: getFeedbackCountCell(model),
						actions: getActionsColumn(model),
					};
					if (isSmallScreen) {
						const setLoadingRowForCta = (loadingRow?: { data: TgModel; column: string }) =>
							setLoadingRow(loadingRow ? { ...loadingRow, column: "cta_stacked" } : undefined);
						base["cta_stacked"] = (
							<Box className={columnStyles.ctaStacked}>
								{getIntegrateNowButton(model)}
								{getPossibleAction(model, setLoadingRowForCta)}
							</Box>
						);
					} else {
						base["get_access"] = getIntegrateNowButton(model);
						base["possible_action"] = getPossibleAction(model, setLoadingRow);
					}
					return base;
				})(),
			};
		};

		const expertTableData = models.map((model) => createExpertRow(toJS(model)));

		return (
			<Box className={styles.tableSection}>
				<Typography className={styles.tableTitle}>{t("expertTitle")}</Typography>
				<TableLayout
					headerArray={expertTableHeaderArray}
					tableData={expertTableData}
					paginationOptions={paginationOptions}
					loadingRow={loadingRow}
					hideHeader
					minTableHeight={"100px"}
					columnOrderKeys={
						isSmallScreen
							? [
									"name",
									"id",
									"creation_by",
									"status",
									"model_parameters",
									"active_model_version",
									"actions",
									"cta_stacked",
								]
							: [
									"name",
									"id",
									"creation_by",
									"status",
									"model_parameters",
									"active_model_version",
									"actions",
									"get_access",
									"possible_action",
								]
					}
					columnClassNames={{
						name: columnStyles.colName,
						id: columnStyles.colId,
						creation_by: columnStyles.colCreationBy,
						status: columnStyles.colStatus,
						model_parameters: columnStyles.colModelParameters,
						actions: columnStyles.colActions,
						get_access: columnStyles.colGetAccess,
						possible_action: columnStyles.colPossibleAction,
						active_model_version: columnStyles.colActiveModelVersion,
						cta_stacked: columnStyles.colCtaStacked,
					}}
					headerComponents={headerComponents}
					loadingCellClassName={isSmallScreen ? columnStyles.ctaStackedCenterLoading : undefined}
				/>
			</Box>
		);
	},
);

export default ExpertModelsTable;
